import {
  getCoreRowModel,
  useReactTable,
  type ColumnDef,
  type PaginationState,
  type SortingState,
} from '@tanstack/react-table'
import * as React from 'react'
import { useQuery } from '@tanstack/react-query'

import { DataTableContent } from './DataTableContent'
import { DataTableFooter } from './DataTableFooter'
import { DataTablePagination } from './DataTablePagination'
import { DataTableProvider } from './DataTableProvider'
import { DataTableSearch } from './DataTableSearch'

/**
 * The fleet's DataTable, asking a function instead of a database.
 *
 * The fleet's copy takes a `DataSource` and a `SelectQuery` and composes SQL in
 * the browser (`@jakub.knejzlik/ts-query`); this console has no such thing in
 * front of it — every read goes through the Worker to `sitebrew-api` as REST,
 * and the API is the side that decides what a caller may see (design note 0001
 * §2). So the one thing that differs is the data layer, exactly as agreed: the
 * table owns paging, sorting and search *as state*, and hands them to a
 * `queryFn` the screen provides. Everything drawn — header, rows, footer,
 * pager, the loading veil — is the fleet's, unchanged.
 *
 * What that buys is a seam at one function: the day `/v1/workspaces/:id/sites`
 * grows `?page=`, `?q=` and `?sort=`, `sitesPage` starts passing them on and no
 * component here changes.
 */

/** What the table knows when it asks for a page. */
export interface DataTableQuery {
  /** Zero-based, as TanStack counts. */
  pageIndex: number
  pageSize: number
  /** The global search box, trimmed; `''` when empty. */
  search: string
  sorting: { id: string; desc: boolean }[]
}

/**
 * One page, and how many rows there are in total.
 *
 * `total` is the count *after* filtering and before paging — it is what the
 * pager divides, so a total that forgot the search would draw pages that answer
 * empty.
 */
export interface DataTablePage<RecordType> {
  rows: RecordType[]
  total: number
}

interface DataTableProps<RecordType> {
  columns: ColumnDef<RecordType, any>[]
  /**
   * The cache key this table's pages hang under. The query state (page, search,
   * sorting) is appended here, so the caller writes the key of the *resource* —
   * and invalidating that key still invalidates every page of it.
   */
  queryKey: readonly unknown[]
  queryFn: (query: DataTableQuery) => Promise<DataTablePage<RecordType>>
  pageSize?: number
  onRowClick?: (row: RecordType) => void
  /** Row identity, for React keys and `data-row-id`. Defaults to `row.id`. */
  getRowId?: (row: RecordType) => string
  /** Controlled search value (see `useResourceSearch`). */
  searchValue?: string
  onSearchChange?: (value: string) => void
  searchPlaceholder?: string
  /**
   * Whether this table is searchable at all. Search is not inferred from a list
   * of searchable columns as in the fleet — the `queryFn` is what decides what
   * a search term matches, and it cannot be read off the column definitions.
   */
  searchEnabled?: boolean
  /** A loading state the screen knows about and the query does not. */
  isLoading?: boolean
  /**
   * Which columns are drawn, by column id — `false` leaves one out.
   *
   * The fleet keeps this as state behind a chooser somebody operates; here it
   * is a prop, because the only thing that hides a column in this console is
   * the width of the screen. Four columns on a 390px phone drew two headers on
   * top of each other and clipped two more to a single character (the
   * screenshot run, `sites-mobile`), so the screen says which facts survive
   * that viewport — in one place, rather than per cell.
   */
  columnVisibility?: Record<string, boolean>
  children?: React.ReactNode
}

const DEFAULT_PAGE_SIZE = 20

const DataTableBase = <RecordType,>({
  columns,
  queryKey,
  queryFn,
  pageSize = DEFAULT_PAGE_SIZE,
  onRowClick,
  getRowId,
  searchValue: searchValueProp,
  onSearchChange,
  searchPlaceholder,
  searchEnabled,
  isLoading: isLoadingProp = false,
  columnVisibility,
  children,
}: React.PropsWithChildren<DataTableProps<RecordType>>) => {
  const [sorting, setSorting] = React.useState<SortingState>([])
  const [pagination, setPagination] = React.useState<PaginationState>({
    pageIndex: 0,
    pageSize,
  })

  const [internalSearchValue, setInternalSearchValue] = React.useState('')
  const searchValue = searchValueProp ?? internalSearchValue
  const handleSearchChange = React.useCallback(
    (value: string) => {
      if (onSearchChange) {
        onSearchChange(value)
      } else {
        setInternalSearchValue(value)
      }
    },
    [onSearchChange],
  )

  // Back to the first page whenever the search changes — the fleet's rule, and
  // for its reason: page 4 of a narrower result set is usually empty, and an
  // empty table reads as "nothing matched" rather than as "you are past the
  // end". The first render is skipped so a table that opens on a search term
  // (from the URL, say) does not reset the page it was asked for.
  const isFirstRender = React.useRef(true)
  React.useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false
      return
    }
    setPagination((prev) => (prev.pageIndex === 0 ? prev : { ...prev, pageIndex: 0 }))
  }, [searchValue])

  const search = searchValue.trim()
  const query: DataTableQuery = {
    pageIndex: pagination.pageIndex,
    pageSize: pagination.pageSize,
    search,
    sorting: sorting.map(({ id, desc }) => ({ id, desc })),
  }

  const { data, isFetching, error } = useQuery({
    queryKey: [...queryKey, query],
    queryFn: () => queryFn(query),
    // The previous page stays on screen while the next one is fetched, so
    // paging does not blink through an empty table. The veil says it is busy.
    placeholderData: (previousData) => previousData,
  })

  const defaultGetRowId = React.useCallback((row: RecordType) => (row as { id: string }).id, [])

  const table = useReactTable({
    data: data?.rows ?? [],
    columns,
    getRowId: getRowId ?? defaultGetRowId,
    manualSorting: true,
    manualFiltering: true,
    manualPagination: true,
    rowCount: data?.total ?? 0,
    onSortingChange: setSorting,
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    state: {
      sorting,
      pagination,
      columnPinning: { left: [], right: ['actions'] },
      // Fully controlled, and deliberately without an `onColumnVisibilityChange`
      // to match: nothing in this console toggles a column, so a setter would
      // be a second source for a value the screen already decides.
      columnVisibility: columnVisibility ?? {},
    },
  })

  return (
    <DataTableProvider
      table={table}
      isLoading={isFetching || isLoadingProp}
      error={(error as Error | null) ?? null}
      onRowClick={onRowClick}
      searchValue={searchValue}
      onSearchChange={handleSearchChange}
      searchPlaceholder={searchPlaceholder}
      searchEnabled={searchEnabled}
    >
      <div className="w-full">
        {children ?? (
          <>
            <div className="rounded-sm border">
              <DataTableContent<RecordType> />
            </div>
            <div className="flex items-center justify-end space-x-2 py-4">
              <DataTableFooter />
            </div>
          </>
        )}
      </div>
    </DataTableProvider>
  )
}

type DataTableWithCompound = typeof DataTableBase & {
  displayName?: string
  Content: typeof DataTableContent
  Pagination: typeof DataTablePagination
  Search: typeof DataTableSearch
}

export const DataTable = DataTableBase as DataTableWithCompound
DataTable.Content = DataTableContent
DataTable.Pagination = DataTablePagination
DataTable.Search = DataTableSearch

DataTable.displayName = 'DataTable'
